/**
 * User investigation — every account (all time) with its redemption verdict,
 * and the detail page for one account (?account=<email or TT user ID>).
 * Data: GET /analytics/tt/accounts, and AccountDetail's endpoints.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, Ban, CircleCheck, Search, Users } from "lucide-react";
import { Button, Segmented } from "../../../design/primitives";
import { useTTQuery } from "../ttApi";
import { count, dateOnly, dateTime, money } from "../ttFormat";
import {
  INK,
  LoadState,
  Note,
  PartyPill,
  Pager,
  ReviewCard,
  SearchBox,
  Select,
  TONE,
  Table,
  Tile,
  Tiles,
  VerdictPill,
  Wrap,
} from "../ttKit";
import AccountDetail from "./AccountDetail";

const PAGE_SIZE = 25;

const SORTS = [
  { value: "verdict", label: "Verdict" },
  { value: "completed_usd", label: "Completed $" },
  { value: "fraud_declines", label: "Fraud declines" },
  { value: "attempts", label: "Attempts" },
  { value: "cards", label: "Cards" },
  { value: "devices", label: "Devices" },
  { value: "last_seen", label: "Last seen" },
  { value: "first_seen", label: "First seen" },
  { value: "email", label: "Email" },
];
const ASCENDING_BY_DEFAULT = ["verdict", "email"];

export default function UserInvestigationPanel({ fresh }) {
  const [params, setParams] = useSearchParams();
  const account = params.get("account");

  const openAccount = useCallback(
    (acct) => {
      const next = new URLSearchParams(params);
      if (acct) next.set("account", acct);
      else next.delete("account");
      setParams(next);
    },
    [params, setParams]
  );

  if (account) {
    return <AccountDetail account={account} fresh={fresh} onOpenAccount={openAccount} onBack={() => openAccount(null)} />;
  }
  return <AccountsList fresh={fresh} onOpenAccount={openAccount} />;
}

function AccountsList({ fresh, onOpenAccount }) {
  const [q, setQ] = useState("");
  const [verdict, setVerdict] = useState("");
  const [sort, setSort] = useState("verdict");
  const [dir, setDir] = useState("asc");
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [q, verdict, sort, dir]);

  const state = useTTQuery("accounts", { q, verdict, sort, dir, page, limit: PAGE_SIZE, fresh });
  const vc = state.data?.verdict_counts || {};
  const all = Object.values(vc).reduce((a, b) => a + b, 0);

  const changeSort = (value) => {
    setSort(value);
    setDir(ASCENDING_BY_DEFAULT.includes(value) ? "asc" : "desc");
  };

  return (
    <>
      {state.data && (
        <Tiles>
          <Tile tone="blue" icon={Users} label="Accounts" value={count(all)} sub="Scored for Titan Treasure, all time" />
          <Tile tone="red" icon={Ban} label="Hold" value={count(vc.HOLD || 0)} sub="Do not pay; escalate" />
          <Tile tone="amber" icon={Search} label="Review" value={count(vc.REVIEW || 0)} sub="Check before paying" />
          <Tile tone="green" icon={CircleCheck} label="OK" value={count(vc.OK || 0)} sub="No hold or review reason" />
        </Tiles>
      )}
      <ReviewCard
        eyebrow="Investigation"
        title="Every account"
        desc="Each account with its redemption verdict. Search, filter by verdict, or click a row to open the account."
        right={
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <SearchBox value={q} onChange={setQ} placeholder="Search email or Titan Treasure user ID" />
            <Select label="Sort" value={sort} onChange={changeSort} options={SORTS} />
            <Button
              size="sm"
              variant="ghost"
              aria-label={dir === "asc" ? "Ascending" : "Descending"}
              onClick={() => setDir((d) => (d === "asc" ? "desc" : "asc"))}
              iconLeft={dir === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
            >
              {dir === "asc" ? "Asc" : "Desc"}
            </Button>
          </div>
        }
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
          <Segmented
            size="sm"
            value={verdict}
            onChange={setVerdict}
            options={[
              { value: "", label: `All ${all ? `(${count(all)})` : ""}` },
              { value: "HOLD", label: `Hold (${count(vc.HOLD || 0)})` },
              { value: "REVIEW", label: `Review (${count(vc.REVIEW || 0)})` },
              { value: "OK", label: `OK (${count(vc.OK || 0)})` },
            ]}
          />
          <Note>
            Verdicts and counts use each account's full history
            {state.data?.as_of ? `, as of ${dateTime(state.data.as_of)}` : ""}. Click a row to open the account.
          </Note>
        </div>

        <LoadState state={state} height={320}>
          {(d) => (
            <>
              <Table
                emptyState="No accounts match."
                columns={COLUMNS}
                rows={d.rows.map((r) => ({ key: r.email, ...r, onClick: () => onOpenAccount(r.email) }))}
              />
              <Pager page={page} limit={PAGE_SIZE} total={d.total} onPage={setPage} />
            </>
          )}
        </LoadState>
      </ReviewCard>
    </>
  );
}

const COLUMNS = [
  {
    key: "email",
    label: "Account",
    render: (r) => (
      <div style={{ padding: "4px 0" }}>
        <div style={{ color: TONE.blue.text }}>{r.email}</div>
        {r.account_name && <div style={{ fontSize: 11.5, color: INK.muted }}>{r.account_name}</div>}
      </div>
    ),
  },
  { key: "tt_user_id", label: "TT user ID", render: (r) => r.tt_user_id || "—" },
  { key: "verdict", label: "Verdict", render: (r) => <VerdictPill verdict={r.verdict} /> },
  { key: "reasons", label: "Reasons", render: (r) => <Wrap max={300}>{(r.reasons || []).join("; ") || "—"}</Wrap> },
  { key: "party_status", label: "List status", render: (r) => <PartyPill status={r.party_status} /> },
  { key: "cards", label: "Cards", numeric: true, render: (r) => count(r.cards) },
  { key: "attempts", label: "Attempts", numeric: true, render: (r) => count(r.attempts) },
  {
    key: "fraud_declines",
    label: "Fraud declines",
    numeric: true,
    render: (r) => (r.fraud_declines ? `${count(r.fraud_declines)} · ${money(r.fraud_decline_usd)}` : "0"),
  },
  { key: "completed_usd", label: "Completed", numeric: true, render: (r) => money(r.completed_usd) },
  { key: "first_seen", label: "First seen", render: (r) => dateOnly(r.first_seen) },
  { key: "last_seen", label: "Last seen", render: (r) => dateOnly(r.last_seen) },
];
